import React, { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  usePropertyField,
  useSlotGroup,
  useCollectionBinding,
} from '@vibesites/runtime/react';
import {
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import {
  repliWhiteLogoUrl,
  multihubLogoMonoWhiteUrl,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  YouTubeIcon,
  TikTokIcon,
  LinkedInIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Why this fork still exists
// ----------------------------------------------------------------------------
// LPM Apartments drops the inherited ORA award badge, always shows the
// live-site CORT "Furnish Your Home with CORT" CTA under office hours
// (LPM form id fallback when custom_cort_lead_form_id is unset), and
// replaces the Google review button with the live-site Apartment Ratings
// link, and orders columns Contact · Hours · Logo · Compliance.
// Otherwise this is the Weidner Classic four-column footer.
// ============================================================================

const FOOTER_BG = 'var(--vibe-color-primary)';
const FOOTER_CTA = 'var(--vibe-color-secondary)';
const FOOTER_FG = 'var(--vibe-color-text-on-primary)';

// CORT furnished-apartments lead form — mirrors `@vibesites/blocks` cort-lead-form
// so the footer CTA can open `window.Cort_modal` without requiring a separate
// injection-block mount in the editor.
const CORT_SCRIPT_ID = 'CortFormLoader';
const CORT_FORM_DIV_ID = 'CortFormDiv';
const CORT_SCRIPT_URL = 'https://api-assets.cort.com/scripts/CortFormLoader.js';
const CORT_STYLE_URLS = [
  'https://api-assets.cort.com/styles/tingle.min.css',
  'https://api-assets.cort.com/styles/cort_leadform_v2.css',
] as const;
const CORT_TINGLE_SCRIPT_ID = 'CortTingleScript';
const CORT_TINGLE_SCRIPT_URL = 'https://api-assets.cort.com/scripts/tingle.js';
// Live LPM CORT lead form — https://www.lpmapartments.com/
const LPM_CORT_FORM_ID = 'LPM_yVudA9sp9XnRlHnOc81z';

type CortModalApi = { open?: () => void };
type CortWindow = Window & {
  Cort_modal?: CortModalApi;
  loadCortForm?: (id: string, mode?: string) => void | Promise<void>;
};

function getCortWindow(): CortWindow | undefined {
  if (typeof window === 'undefined') return undefined;
  return window as CortWindow;
}

function getCortModal(): CortModalApi | undefined {
  return getCortWindow()?.Cort_modal;
}

function ensureCortStylesheets() {
  if (typeof document === 'undefined') return;
  for (const href of CORT_STYLE_URLS) {
    if (document.querySelector(`link[data-cort-style="${href}"]`)) continue;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.crossOrigin = 'anonymous';
    link.setAttribute('data-cort-style', href);
    document.head.appendChild(link);
  }
}

function ensureCortFormHost() {
  if (typeof document === 'undefined') return;
  // Leave #CortFormDiv visible in the document flow the same way the live
  // LPM site does. Custom mode hides CORT's own button; clipping this host
  // also clips #Cortpopup and can prevent tingle from copying modal markup.
  let formDiv = document.getElementById(CORT_FORM_DIV_ID);
  if (!formDiv) {
    formDiv = document.createElement('div');
    formDiv.id = CORT_FORM_DIV_ID;
    document.body.appendChild(formDiv);
  } else {
    formDiv.removeAttribute('aria-hidden');
    formDiv.removeAttribute('hidden');
    formDiv.style.cssText = '';
  }
}

function ensureCortTingleScript(): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  const win = window as Window & { tingle?: unknown };
  if (win.tingle) return Promise.resolve();

  const existing = document.getElementById(CORT_TINGLE_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      if ((window as Window & { tingle?: unknown }).tingle) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('CORT tingle script failed to load')), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = CORT_TINGLE_SCRIPT_ID;
    script.src = CORT_TINGLE_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('CORT tingle script failed to load'));
    document.body.appendChild(script);
  });
}

function loadCortScript(): Promise<void> {
  const win = getCortWindow();
  if (!win || typeof document === 'undefined') return Promise.resolve();
  if (typeof win.loadCortForm === 'function') return Promise.resolve();

  const existing = document.getElementById(CORT_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      if (typeof getCortWindow()?.loadCortForm === 'function') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('CORT script failed to load')), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = CORT_SCRIPT_ID;
    script.src = CORT_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('CORT script failed to load'));
    document.body.appendChild(script);
  });
}

function waitForCortModal(timeoutMs = 15000): Promise<CortModalApi | undefined> {
  const existing = getCortModal();
  if (existing?.open) return Promise.resolve(existing);

  return new Promise((resolve) => {
    const started = Date.now();
    const timer = window.setInterval(() => {
      const modal = getCortModal();
      if (modal?.open) {
        window.clearInterval(timer);
        resolve(modal);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        window.clearInterval(timer);
        resolve(undefined);
      }
    }, 100);
  });
}

async function ensureCortFormReady(formId: string): Promise<CortModalApi | undefined> {
  const ready = getCortModal();
  if (ready?.open) return ready;

  ensureCortStylesheets();
  ensureCortFormHost();
  await Promise.all([loadCortScript(), ensureCortTingleScript()]);

  const win = getCortWindow();
  if (typeof win?.loadCortForm === 'function') {
    await Promise.resolve(win.loadCortForm(formId, 'Custom'));
  }

  return waitForCortModal();
}

// ============================================================================
// Types
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

/** Split a single-line address into street / city-state lines at the first comma. */
function formatAddressLines(address: string): string[] {
  const trimmed = address.trim();
  if (!trimmed) return [];
  const idx = trimmed.indexOf(',');
  if (idx === -1) return [trimmed];
  const street = trimmed.slice(0, idx).trim();
  const rest = trimmed.slice(idx + 1).trim();
  return rest ? [street, rest] : [street];
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const byDay: Record<string, OfficeHour> = {};
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.indexOf(key) === -1) continue;
    if (!(key in byDay)) byDay[key] = entry;
  }

  const groups: GroupedHours[] = [];

  for (const key of DAY_ORDER) {
    const entry = byDay[key];
    const dayLabel = DAY_LABELS[key];
    const dayShortLabel = DAY_SHORT_LABELS[key];

    const isClosed =
      !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry!.open_time || '')} - ${formatTime(entry!.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    url: { label: 'URL', type: 'url', defaultField: 'property_google_place_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const getDirectionsLinkDef: SlotGroupDefinition = {
  label: 'Get Directions Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Get Directions →' },
    url: { label: 'URL', type: 'url', defaultField: 'property_google_place_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const textUsLinkDef: SlotGroupDefinition = {
  label: 'Text Us Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Text Us!' },
    url: { label: 'URL', type: 'url', defaultField: 'custom_text_us_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const apartmentsRatingLinkDef: SlotGroupDefinition = {
  label: 'Apartment Ratings Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'REVIEW US ON APARTMENT RATINGS' },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apartments_url',
      suggestedFields: ['property_apartments_url'],
      defaultValue: 'https://www.apartments.com/lpm-apartments-minneapolis-mn/htc4991',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookSocialDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramSocialDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const twitterSocialDef: SlotGroupDefinition = {
  label: 'Twitter / X',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_twitter_url',
      suggestedFields: ['property_twitter_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const youtubeSocialDef: SlotGroupDefinition = {
  label: 'YouTube',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.youtube.com/@WeidnerApartmentHomes',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const tiktokSocialDef: SlotGroupDefinition = {
  label: 'TikTok',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_tiktok_url',
      suggestedFields: ['property_tiktok_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const linkedinSocialDef: SlotGroupDefinition = {
  label: 'LinkedIn',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_linkedin_url',
      suggestedFields: ['property_linkedin_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');
  const cortButtonText = useContentToken('footer.cort_button_text');
  const copyright = useContentToken('footer.copyright');
  const copyrightText =
    (copyright || '').trim() || `© ${new Date().getFullYear()} All Rights Reserved.`;
  const cortLeadFormId = ((usePropertyField('custom_cort_lead_form_id') as string) || LPM_CORT_FORM_ID).trim();

  useEffect(() => {
    if (!cortLeadFormId) return;
    let cancelled = false;
    void ensureCortFormReady(cortLeadFormId).then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [cortLeadFormId]);

  const openCortModal = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault();
      event?.stopPropagation();
      (event?.nativeEvent as Event & { stopImmediatePropagation?: () => void } | undefined)
        ?.stopImmediatePropagation?.();

      if (!cortLeadFormId) return;

      void (async () => {
        const modal = await ensureCortFormReady(cortLeadFormId);
        modal?.open?.();
      })();
    },
    [cortLeadFormId],
  );

  // Footer surface is always primary — always prefer white/mono-white logos.
  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  const getDirectionsLink = useSlotGroup('footer.get_directions_link', getDirectionsLinkDef);
  const textUsLink = useSlotGroup('footer.text_us_link', textUsLinkDef);
  const apartmentsRatingLink = useSlotGroup('footer.apartments_rating_link', apartmentsRatingLinkDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  const facebookSocial = useSlotGroup('footer.facebook_social', facebookSocialDef);
  const instagramSocial = useSlotGroup('footer.instagram_social', instagramSocialDef);
  const twitterSocial = useSlotGroup('footer.twitter_social', twitterSocialDef);
  const youtubeSocial = useSlotGroup('footer.youtube_social', youtubeSocialDef);
  const tiktokSocial = useSlotGroup('footer.tiktok_social', tiktokSocialDef);
  const linkedinSocial = useSlotGroup('footer.linkedin_social', linkedinSocialDef);

  const hasAnySocial =
    hasValue(facebookSocial.url) ||
    hasValue(instagramSocial.url) ||
    hasValue(twitterSocial.url) ||
    hasValue(youtubeSocial.url) ||
    hasValue(tiktokSocial.url) ||
    hasValue(linkedinSocial.url);

  const showTextUsLink = hasValue(textUsLink.url);
  const apartmentsRatingUrl =
    apartmentsRatingLink.url || 'https://www.apartments.com/lpm-apartments-minneapolis-mn/htc4991';
  const showApartmentsRating = hasValue(apartmentsRatingUrl);
  const showGetDirections = hasValue(getDirectionsLink.url) || hasValue(addressLink.url);
  const addressLines = formatAddressLines(addressLink.text || '123 Main Street, City, ST 00000');

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // Synced from `@vibesites/theme-rise/components/Footer.tsx`. Footer is the
  // global mount point so the chatbot stays above sticky chrome.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const PATTERNS = [
      /\brcc-app/i,
      /\bwebchat-toggle/i,
      /conversations-webchat/i,
      /rentcafe/i,
      /rciq/i,
      /\bcrm[-_ ]?iq\b/i,
      /\biq[-_ ]?(chat|bot)/i,
      /yardi/i,
      /yvenue/i,
    ];

    const Z = '2147483647';

    function attrHaystack(el: Element): string {
      const id = el.id || '';
      const cls = typeof el.className === 'string'
        ? el.className
        : ((el.className as unknown as { baseVal?: string })?.baseVal || '');
      const src = el.getAttribute('src') || '';
      const title = el.getAttribute('title') || '';
      const name = el.getAttribute('name') || '';
      let data = '';
      for (const a of Array.from(el.attributes)) {
        if (a.name.startsWith('data-')) data += ` ${a.name} ${a.value}`;
      }
      return `${id} ${cls} ${src} ${title} ${name}${data}`;
    }

    function looksLikeRciq(el: Element): boolean {
      const hay = attrHaystack(el);
      return PATTERNS.some(p => p.test(hay));
    }

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        const pos = getComputedStyle(cur).position;
        if (pos === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!looksLikeRciq(el)) return;
      pin(el);
      const fixedAncestor = outermostFixedAncestor(el);
      if (fixedAncestor && fixedAncestor !== el) pin(fixedAncestor);
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll('iframe, div, aside, section, button');
      for (const c of Array.from(candidates)) processCandidate(c);
      if (root instanceof Element) processCandidate(root);
    }

    scanSubtree(document.body);

    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'class', 'src', 'title', 'name', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer
      style={{ backgroundColor: FOOTER_BG, color: FOOTER_FG }}
    >
      {/* ---- Main Footer Content (4 columns) ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 md:pt-14 md:pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 items-start">

          {/* Column 1: Contact */}
          <div className="flex flex-col items-center sm:items-start text-center sm:text-left gap-3">
            <RichTextContent
              path="footer.contact_us_label"
              defaultValue="<p>CONTACT US</p>"
              as="p"
              className="footer-section-heading"
              style={{
                color: FOOTER_FG,
                fontFamily: 'var(--vibe-font-body)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                lineHeight: 1.15,
              }}
            />

            <a
              href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
              className="dniphone dniphonehref font-body text-base text-inherit hover:opacity-70 transition-opacity"
              style={{ fontFamily: 'var(--vibe-font-body)' }}
            >
              <EditableToken path="footer.phone">
                {formatPhone(phone)}
              </EditableToken>
            </a>

            {showTextUsLink && (
              <EditableSlotGroup groupPath="footer.text_us_link" definition={textUsLinkDef}>
                <a
                  href={textUsLink.url || '#'}
                  target={textUsLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 font-body text-sm text-inherit underline underline-offset-4 hover:opacity-70 transition-opacity"
                  style={{ fontFamily: 'var(--vibe-font-body)' }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className="shrink-0"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span>{textUsLink.text || 'Text Us!'}</span>
                </a>
              </EditableSlotGroup>
            )}

            <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
              <a
                href={addressLink.url || '#'}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="font-body text-sm text-inherit hover:opacity-70 transition-opacity block leading-relaxed"
              >
                {addressLines.map((line, i) => (
                  <span key={i} className="block">{line}</span>
                ))}
              </a>
            </EditableSlotGroup>

            {showGetDirections && (
              <EditableSlotGroup groupPath="footer.get_directions_link" definition={getDirectionsLinkDef}>
                <a
                  href={getDirectionsLink.url || addressLink.url || '#'}
                  target={getDirectionsLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="font-body text-sm font-semibold hover:opacity-80 transition-opacity mt-1"
                  style={{ color: FOOTER_CTA, fontFamily: 'var(--vibe-font-body)' }}
                >
                  {getDirectionsLink.text || 'Get Directions →'}
                </a>
              </EditableSlotGroup>
            )}
          </div>

          {/* Column 2: Office Hours · CORT */}
          <div className="flex min-w-0 flex-col items-center overflow-visible text-center sm:items-start sm:text-left gap-3">
            <RichTextContent
              path="footer.office_hours_label"
              defaultValue="<p>OFFICE HOURS</p>"
              as="p"
              className="footer-section-heading"
              style={{
                color: FOOTER_FG,
                fontFamily: 'var(--vibe-font-body)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                lineHeight: 1.15,
              }}
            />
            <EditableCollectionBinding
              bindingPath="footer.office_hours"
              label="Office Hours"
            >
              <div className="w-full max-w-[280px] flex flex-col">
                {groupedHours.length > 0 ? (
                  <dl className="space-y-2 w-full">
                    {groupedHours.map((group, index) => (
                      <div key={index} className="flex justify-between font-body text-sm text-inherit">
                        <dt>
                          <span className="sm:hidden">{group.label}</span>
                          <span className="hidden sm:inline">{group.shortLabel}</span>
                        </dt>
                        <dd className="text-right ml-4">{group.hours}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="font-body text-sm opacity-50">Office hours will be displayed here.</p>
                )}
              </div>
            </EditableCollectionBinding>

            {cortLeadFormId ? (
              <div className="mt-1 flex w-max max-w-none justify-center sm:justify-start">
                <button
                  type="button"
                  onClickCapture={openCortModal}
                  className="footer-cort-btn group/cort relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap font-semibold uppercase transition-colors duration-300"
                  style={{
                    color: 'var(--vibe-color-text-on-secondary)',
                    background: FOOTER_CTA,
                  }}
                >
                  <svg className="footer-cort-btn-icon relative z-10 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2.5l2.83 6.72 7.32.62-5.55 4.74 1.66 7.17L12 17.77 5.74 21.75l1.66-7.17-5.55-4.74 7.32-.62L12 2.5z" />
                  </svg>
                  <EditableToken path="footer.cort_button_text" as="span" className="footer-cort-btn-label relative z-10 pointer-events-none">
                    {cortButtonText || 'Furnish Your Home with CORT'}
                  </EditableToken>
                </button>
              </div>
            ) : null}
          </div>

          {/* Column 3: Brand logo + Apartment Ratings */}
          <div className="flex flex-col items-center text-center gap-6">
            <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    sizes="220px"
                    className="h-24 w-auto max-w-[220px] object-contain md:h-28"
                  />
                ) : (
                  <div className="font-heading text-2xl text-inherit">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            {showApartmentsRating && (
              <EditableSlotGroup groupPath="footer.apartments_rating_link" definition={apartmentsRatingLinkDef}>
                <a
                  href={apartmentsRatingUrl}
                  target={apartmentsRatingLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="footer-ratings-link font-body text-sm font-bold uppercase underline underline-offset-4 hover:opacity-80 transition-opacity"
                  style={{
                    color: FOOTER_FG,
                    fontFamily: 'var(--vibe-font-body)',
                  }}
                >
                  {apartmentsRatingLink.text || 'REVIEW US ON APARTMENT RATINGS'}
                </a>
              </EditableSlotGroup>
            )}
          </div>

          {/* Column 4: Compliance · Legal · Managed By · Social
              Top band matches the section-heading height so icons sit in the
              label row; legal links then share the same gap-3 offset as hours. */}
          <div className="flex flex-col items-center text-center">
            <div className="footer-compliance-top flex w-full flex-col items-center justify-end">
              <div className="flex items-center justify-center gap-5 text-inherit">
                <WheelchairIcon size={26} color="currentColor" />
                <FairHousingIcon size={26} color="currentColor" />
                <PetsAllowedIcon size={26} color="currentColor" />
              </div>
            </div>

            <nav
              className="mt-3 flex flex-nowrap items-center justify-center gap-x-1.5 whitespace-nowrap font-body text-[13px] text-inherit"
              aria-label="Legal links"
            >
              <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:opacity-70 transition-opacity">
                  {accessibilityLink.text || 'Accessibility'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true">•</span>
              <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:opacity-70 transition-opacity">
                  {privacyPolicyLink.text || 'Privacy Policy'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true">•</span>
              <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:opacity-70 transition-opacity">
                  {sitemapLink.text || 'Sitemap'}
                </a>
              </EditableSlotGroup>
            </nav>

            <EditableSlotGroup
              groupPath="footer.managed_by_logo"
              definition={managedByLogoDef}
              className="mt-4 flex flex-col items-center gap-2"
            >
              {managedByLogo.image ? (
                <a
                  href={managedByLogo.url || '#'}
                  target={managedByLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
                >
                  <RichTextContent
                    path="footer.managed_by_text"
                    defaultValue="<p>Professionally Owned &amp; Managed by</p>"
                    as="span"
                    className="font-body text-[11px] text-inherit"
                  />
                  <VibesitesImage
                    url={managedByLogo.image}
                    alt={managedByLogo.alt || 'Property Management Company'}
                    sizes="140px"
                    className="h-10 w-auto max-w-[140px] object-contain"
                  />
                </a>
              ) : null}
            </EditableSlotGroup>

            {hasAnySocial && (
              <div className="flex items-center justify-center gap-4 text-inherit mt-1">
                {hasValue(facebookSocial.url) && (
                  <EditableSlotGroup groupPath="footer.facebook_social" definition={facebookSocialDef}>
                    <a
                      href={facebookSocial.url || '#'}
                      target={facebookSocial._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="inline-flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <FacebookIcon size={18} color="currentColor" aria-label="Facebook" />
                    </a>
                  </EditableSlotGroup>
                )}
                {hasValue(instagramSocial.url) && (
                  <EditableSlotGroup groupPath="footer.instagram_social" definition={instagramSocialDef}>
                    <a
                      href={instagramSocial.url || '#'}
                      target={instagramSocial._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="inline-flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <InstagramIcon size={18} color="currentColor" aria-label="Instagram" />
                    </a>
                  </EditableSlotGroup>
                )}
                {hasValue(twitterSocial.url) && (
                  <EditableSlotGroup groupPath="footer.twitter_social" definition={twitterSocialDef}>
                    <a
                      href={twitterSocial.url || '#'}
                      target={twitterSocial._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Twitter / X"
                      className="inline-flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <TwitterIcon size={18} color="currentColor" aria-label="Twitter / X" />
                    </a>
                  </EditableSlotGroup>
                )}
                {hasValue(youtubeSocial.url) && (
                  <EditableSlotGroup groupPath="footer.youtube_social" definition={youtubeSocialDef}>
                    <a
                      href={youtubeSocial.url || '#'}
                      target={youtubeSocial._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="YouTube"
                      className="inline-flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <YouTubeIcon size={18} color="currentColor" aria-label="YouTube" />
                    </a>
                  </EditableSlotGroup>
                )}
                {hasValue(tiktokSocial.url) && (
                  <EditableSlotGroup groupPath="footer.tiktok_social" definition={tiktokSocialDef}>
                    <a
                      href={tiktokSocial.url || '#'}
                      target={tiktokSocial._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="TikTok"
                      className="inline-flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <TikTokIcon size={18} color="currentColor" aria-label="TikTok" />
                    </a>
                  </EditableSlotGroup>
                )}
                {hasValue(linkedinSocial.url) && (
                  <EditableSlotGroup groupPath="footer.linkedin_social" definition={linkedinSocialDef}>
                    <a
                      href={linkedinSocial.url || '#'}
                      target={linkedinSocial._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      className="inline-flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <LinkedInIcon size={18} color="currentColor" aria-label="LinkedIn" />
                    </a>
                  </EditableSlotGroup>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ---- Bottom bar: powered by (left) · identity + copyright (center) · design by (right) ---- */}
      <div
        style={{
          borderTop: `1px solid color-mix(in srgb, ${FOOTER_FG} 18%, transparent)`,
        }}
      >
        {/* pb-20 lifts the bar clear of floating chat/review launchers (matches Avenue). */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-20 grid grid-cols-1 lg:grid-cols-3 items-center gap-3">
          <div className="flex justify-center lg:justify-start">
            <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
              <a
                href={poweredByLogo.url || 'https://www.multihub.io/'}
                target={poweredByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.powered_by_text"
                  defaultValue="<p>Powered by</p>"
                  as="span"
                  className="font-body text-[10px] text-inherit"
                />
                <VibesitesImage
                  url={multihubLogoMonoWhiteUrl}
                  alt={poweredByLogo.alt || 'Multihub'}
                  height={14}
                  sizes="96px"
                  className="h-3.5 w-auto"
                  priority
                />
              </a>
            </EditableSlotGroup>
          </div>

          <div className="flex flex-col items-center text-center gap-1">
            <RichTextContent
              path="footer.identity_verification_text"
              defaultValue="<p>We participate in identity verification of all applicants.</p>"
              as="p"
              className="font-body text-[12px] text-inherit"
            />
            <EditableToken
              path="footer.copyright"
              as="p"
              className="font-body text-[12px] text-inherit"
            >
              {copyrightText}
            </EditableToken>
          </div>

          <div className="flex justify-center lg:justify-end">
            <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
              <a
                href={designByLogo.url || 'https://www.repli360.com/'}
                target={designByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.design_by_text"
                  defaultValue="<p>Website Design by</p>"
                  as="span"
                  className="font-body text-[10px] text-inherit"
                />
                <VibesitesImage
                  url={repliWhiteLogoUrl}
                  alt={designByLogo.alt || 'Repli Logo'}
                  height={16}
                  sizes="64px"
                  className="h-4 w-auto"
                  priority
                />
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .tingle-modal {
          z-index: 2147483646 !important;
        }

        /* Custom-mode CORT still injects its default button into #CortFormDiv.
           Hide that leftover so only the footer CTA opens the modal. */
        #CortFormDiv #CortLeadForm_button_container {
          display: none !important;
        }

        /* Match Tower 12: bold white sans labels (not the serif display heading).
           Force color through nested richtext marks that may carry editor color. */
        .footer-section-heading,
        .footer-section-heading p,
        .footer-section-heading span {
          margin: 0;
          color: ${FOOTER_FG} !important;
          font-family: var(--vibe-font-body) !important;
          font-size: 1.375rem;
          font-weight: 700 !important;
          line-height: 1.15;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        @media (min-width: 768px) {
          .footer-section-heading,
          .footer-section-heading p,
          .footer-section-heading span {
            font-size: 1.625rem;
          }
        }

        /* Icons occupy the section-heading band; legal links share gap-3 with hours. */
        @media (min-width: 1024px) {
          .footer-compliance-top {
            min-height: calc(1.625rem * 1.15);
          }
        }

        .footer-cort-btn {
          box-sizing: border-box;
          width: max-content;
          max-width: none;
          padding: 0.625rem 1rem;
          font-size: 0.6875rem;
          letter-spacing: 0.04em;
          line-height: 1;
          white-space: nowrap;
          flex-wrap: nowrap;
        }
        .footer-cort-btn:hover {
          background: #b6b6b6 !important;
          color: #000 !important;
        }
        .footer-ratings-link,
        .footer-ratings-link:hover {
          color: ${FOOTER_FG};
        }
        .footer-cort-btn-label {
          flex: none;
          text-align: center;
          line-height: 1;
          white-space: nowrap;
        }
        .footer-cort-btn-icon {
          width: 0.75rem;
          height: 0.75rem;
        }

        @media (min-width: 768px) {
          .footer-cort-btn {
            padding: 0.7rem 1.15rem;
            font-size: 0.75rem;
          }
          .footer-cort-btn-icon {
            width: 0.875rem;
            height: 0.875rem;
          }
        }

        .rcc-app-cta-wrap,
        .rcc-app-controls,
        [class*="rcc-app-"],
        [id*="conversations-webchat"],
        [class*="webchat-toggle"],
        iframe[src*="rentcafeiq"],
        iframe[src*="rentcafe-iq"],
        iframe[src*="rentcafe.com/iq"],
        iframe[src*="yardi"],
        iframe[src*="yvenue"],
        iframe[title*="RentCafe IQ" i],
        iframe[title*="Rent Cafe IQ" i],
        iframe[title*="CRM IQ" i],
        iframe[title*="webchat" i],
        [id^="rciq"],
        [id*="rentcafeiq"],
        [id*="rentcafe-iq"],
        [id*="rentcafe_iq"],
        [id*="iq-chat"],
        [id*="iqchat"],
        [id*="iq-bot"],
        [id*="iqbot"],
        [class^="rciq-"],
        [class*=" rciq-"],
        [class*="rentcafeiq"],
        [class*="rentcafe-iq"],
        [class*="rentcafe_iq"],
        [class*="iq-chat"],
        [class*="iqchat"],
        [class*="yardi-chat"],
        [class*="yvenue"],
        [data-rciq],
        [data-rentcafeiq] {
          z-index: 2147483647 !important;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.contact_us_label': {
      label: 'Contact Us Label',
      type: 'richtext',
      defaultValue: '<p>CONTACT US</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>OFFICE HOURS</p>',
    },
    'footer.cort_button_text': {
      label: 'CORT Button Text',
      type: 'text',
      defaultValue: 'Furnish Your Home with CORT',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Owned &amp; Managed by</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
    'footer.identity_verification_text': {
      label: 'Identity Verification Text',
      type: 'richtext',
      defaultValue: '<p>We participate in identity verification of all applicants.</p>',
    },
    'footer.copyright': {
      label: 'Copyright',
      type: 'text',
      defaultField: 'property_copyright',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.address': addressLinkDef,
    'footer.get_directions_link': getDirectionsLinkDef,
    'footer.text_us_link': textUsLinkDef,
    'footer.apartments_rating_link': apartmentsRatingLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.facebook_social': facebookSocialDef,
    'footer.instagram_social': instagramSocialDef,
    'footer.twitter_social': twitterSocialDef,
    'footer.youtube_social': youtubeSocialDef,
    'footer.tiktok_social': tiktokSocialDef,
    'footer.linkedin_social': linkedinSocialDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Footer;
